import { Text, Icon, Colors } from '@amelio/ui-library';

import type { User } from '@/types/directory/User';

import { AvatarStack } from '@/components/AvatarStack/AvatarStack';
import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';

export interface EvaluationResultInformationProps {
    employeeName: string;
    employeeRole: string;
    evaluationLabel: string;
    invitedCount: number;
    respondedCount: number;
    reviewers: User[];
    dateLabel: string;
    reviewType?: EvaluationTypeValue;
    className?: string;
    dataTestId?: string;
}

interface HeaderTopProps {
    title: string;
    dateLabel: string;
}

interface HeaderMetaProps {
    employeeName: string;
    employeeRole: string;
    evaluationLabel: string;
    invitedCount: number;
    respondedCount: number;
    reviewType?: EvaluationTypeValue;
}

interface MetaLineProps {
    label: string;
    value: string | number;
}

interface StatusItemProps {
    color: string;
    label: string;
    value: number;
}

export const EvaluationResultInformation = ({
    employeeName,
    employeeRole,
    evaluationLabel,
    invitedCount,
    respondedCount,
    reviewers,
    dateLabel,
    reviewType,
    className = '',
    dataTestId = 'evaluation-result-information',
}: EvaluationResultInformationProps) => {
    const { t } = useAmelioTranslations();

    return (
        <section
            className={`w-full rounded-xl border border-white bg-white/45 p-6 ${className}`}
            data-testid={dataTestId}
        >
            <HeaderTop
                title={
                    reviewType === EvaluationType.SELF_EVALUATION
                        ? t('results.selfEvaluationTitle')
                        : t('results.headerTitle')
                }
                dateLabel={dateLabel}
            />

            <HeaderMeta
                employeeName={employeeName}
                employeeRole={employeeRole}
                evaluationLabel={evaluationLabel}
                invitedCount={invitedCount}
                respondedCount={respondedCount}
                reviewType={reviewType}
            />

            <div className={'mt-8 mb-3'}>
                <AvatarStack
                    users={reviewers}
                    size={'3xs'}
                    spacing={'normal'}
                    maxVisible={9}
                    showTooltip
                />
            </div>
        </section>
    );
};

const HeaderTop = ({ title, dateLabel }: HeaderTopProps) => {
    return (
        <div className={'items-start justify-between sm:flex'}>
            <div className={'flex items-center gap-3'}>
                <div className={'flex h-12 w-12 items-center justify-center rounded-full bg-white'}>
                    <Icon
                        name={'arrow-repeat'}
                        size={'lg'}
                        color={Colors.PRIMARY_600}
                    />
                </div>

                <Text
                    size={'large'}
                    weight={'semibold'}
                >
                    {title}
                </Text>
            </div>

            <Text
                size={'small'}
                color={Colors.BASE_400}
                className={'ml-15 sm:ml-auto'}
            >
                {dateLabel}
            </Text>
        </div>
    );
};

const HeaderMeta = ({
    employeeName,
    employeeRole,
    evaluationLabel,
    invitedCount,
    respondedCount,
    reviewType,
}: HeaderMetaProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'mt-6 space-y-1'}>
            <MetaLine
                label={t('results.employee')}
                value={employeeRole ? `${employeeName} – ${employeeRole}` : employeeName}
            />

            <MetaLine
                label={t('results.evaluation')}
                value={evaluationLabel}
            />

            <div className={'flex flex-wrap items-center gap-4 pt-2'}>
                {reviewType !== EvaluationType.SELF_EVALUATION && (
                    <MetaLine
                        label={t('results.reviewersInvited')}
                        value={invitedCount}
                    />
                )}

                <StatusItem
                    color={Colors.SUCCESS}
                    label={t('results.responded')}
                    value={respondedCount}
                />

                <StatusItem
                    color={Colors.ERROR}
                    label={t('results.noResponse')}
                    value={invitedCount - respondedCount}
                />
            </div>
        </div>
    );
};

const MetaLine = ({ label, value }: MetaLineProps) => {
    return (
        <Text
            size={'small'}
            color={Colors.BASE_400}
        >
            <span className={'text-base-600 font-semibold'}>{label}:</span> {value}
        </Text>
    );
};

const StatusItem = ({ color, label, value }: StatusItemProps) => {
    return (
        <div className={'flex items-center gap-2'}>
            <span
                className={'h-2 w-2 rounded-full'}
                style={{ backgroundColor: color }}
                aria-hidden
            />
            <MetaLine
                label={label}
                value={value}
            />
        </div>
    );
};

export { EvaluationResultInformation as default };
